import type { FeatureFlagValueType } from '@brightdrop/feature-flags-client';
import { type Profile, Role } from '@gm-commercial/profile-model';

import BankIcon from '~/assets/icons/new/bank-icon.svg?react';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { CATALOG_AND_PLANS_ACCESS_ROLES } from '~/common/constants/common.constant';
import {
  FLEETIO_ACCOUNT_LINKING_FLAG,
  FUEL_CARD_AND_EXPENSES_FLAG,
} from '~/common/models/featureFlags.model';
import { RouteName } from '~/common/models/route.model';
import { findRoute, makeRoutePath } from '~/common/utils/route/route.utils';

import { getUserNavBarItems } from './user.navBarItems.config';

const generateTestId = (
  base: string,
  value: string,
  suffix = 'nav-list-item'
) => {
  return `${base}-${value}-${suffix}`;
};
export const getOrgNavBarItems = ({
  profileParams,
  findFlagScope,
  hasRole,
  isShopAddOnsEnabled = false,
  showFleetPrograms = true,
  showIntegrations = true,
}: {
  profileParams: Profile['permissionsContextIds'];
  findFlagScope?: (flagName: string) => FeatureFlagValueType | undefined;
  hasRole?: (authorizedRoles: Role[]) => boolean;
  isShopAddOnsEnabled?: boolean;
  /** False while the organization record is loading, when there is no organization, when the lookup fails, and for non-US organizations. */
  showFleetPrograms?: boolean;
  /** False until the organization has an enrollment with at least one partner. */
  showIntegrations?: boolean;
}) => {
  const context = {
    fleetsId: profileParams?.fleetsId || undefined,
    hubsId: profileParams?.hubsId || undefined,
    organizationsId: profileParams?.organizationsId || undefined,
  };

  return [
    {
      label: translateText('account:organization.nav.title'),
      testId: generateTestId('account', 'organization', 'header'),
      icon: <BankIcon className="noFill" />,
      path: makeRoutePath(
        findRoute(RouteName.CS_PLATFORM_ACCOUNT_ORG_DETAILS),
        context,
        true,
        true,
        findFlagScope
      ),
      children: [
        {
          label: translateText('organization:organizationDetails'),
          testId: generateTestId('account-organization', 'details'),
          path: makeRoutePath(
            findRoute(RouteName.CS_PLATFORM_ACCOUNT_ORG_DETAILS),
            {},
            false,
            false,
            findFlagScope
          ),
        },
        ...getUserNavBarItems({
          profileParams,
        }),
        ...(hasRole && hasRole(CATALOG_AND_PLANS_ACCESS_ROLES)
          ? [
              {
                label: translateText(
                  isShopAddOnsEnabled
                    ? 'entitlements:planList.plansBillingTitle'
                    : 'entitlements:planList.title'
                ),
                testId: generateTestId('account-organization', 'plans'),
                path: makeRoutePath(
                  findRoute(RouteName.PLANS_LIST_VIEW),
                  {},
                  true,
                  true
                ),
              },
            ]
          : []),
        ...(showFleetPrograms
          ? [
              {
                label: translateText('organization:fleetPrograms'),
                testId: generateTestId(
                  'account-organization',
                  'fleet-programs'
                ),
                path: makeRoutePath(
                  findRoute(RouteName.CS_PLATFORM_ACCOUNT_FLEET_PROGRAMS),
                  context,
                  true,
                  true,
                  findFlagScope
                ),
              },
            ]
          : []),

        {
          label: translateText('account:organization.nav.legal'),
          testId: generateTestId('account-organization', 'legal'),
          path: makeRoutePath(
            findRoute(RouteName.LEGAL),
            context,
            true,
            true,
            findFlagScope
          ),
        },
        // Org managers only, once a partner whose flag is on is actively linked.
        ...(showIntegrations &&
        hasRole?.([Role.ORG_MANAGER]) &&
        (findFlagScope?.(FLEETIO_ACCOUNT_LINKING_FLAG) ||
          findFlagScope?.(FUEL_CARD_AND_EXPENSES_FLAG))
          ? [
              {
                label: translateText('common:integrations'),
                testId: generateTestId('account-organization', 'integrations'),
                path: makeRoutePath(
                  findRoute(RouteName.ACCOUNT_ORG_INTEGRATIONS),
                  context,
                  true,
                  true,
                  findFlagScope
                ),
              },
            ]
          : []),
      ],
    },
  ];
};
